import {
  getAccessToken,
  usePrivy,
  useSignTypedData as useSignPrivyTypedData,
} from '@privy-io/react-auth';
import { useSignMessage as useSignSolanaMessage } from '@privy-io/react-auth/solana';
import { useMutation } from '@tanstack/react-query';
import { signTypedData } from '@wagmi/core';
import { AxiosError } from 'axios';
import { ethers } from 'ethers';

import config from '@/config';
import useJoinRefCode from '@/features/referrals/api/post-join';
import { usePrivyAccount } from '@/hooks';
import {
  cookieAwarePublicV2Api,
  updatePrimaryPrivateApiHeaders,
  updatePrivateApiHeaders,
} from '@/lib/api-clients/rest-client';
import { useAccountStore } from '@/store/use-account-store';
import { CapitalStatus, EventType, NetworkType } from '@/types';
import { sendEvent } from '@/utils/analytics';

import { uint8ArrayToHex } from '../utils';
import { wagmiConfig } from '../wagmi/config';

type RegisterResponse = {
  apiKey: string;
  accGroup: number;
};

type ExtendedRegisterResponse = RegisterResponse & {
  signingKey: `0x${string}`;
};

type RegisterVariables = {
  address: string;
  rememberMe: boolean;
};

export default function useRegisterUser() {
  const {
    address: primaryAddress,
    networkType,
    isEmbedded,
    disconnect,
  } = usePrivyAccount();
  const { authenticated } = usePrivy();
  const { mutate: submitRefCode } = useJoinRefCode();
  const { signTypedData: signPrivyTypedData } = useSignPrivyTypedData();
  const { signMessage: signSolanaMessage } = useSignSolanaMessage();

  return useMutation<ExtendedRegisterResponse, unknown, RegisterVariables>({
    mutationKey: ['register'],
    mutationFn: async (
      variables: RegisterVariables,
    ): Promise<ExtendedRegisterResponse> => {
      const { address } = variables;

      // Always create a random wallet for signing, regardless of wallet type
      const wallet = ethers.Wallet.createRandom() as unknown as ethers.Wallet;
      const signingAddr = wallet.address.toLowerCase() as `0x${string}`;

      const { getCapitalStatus, getAccurateTime } = useAccountStore.getState();

      const expiryTime = Math.floor(getAccurateTime()) + 1000 * 3600 * 24 * 14; // 2 weeks
      // NOTE: replace to test auth refresh
      // const expiryTime =
      //   Math.floor(useAccountStore.getState().getAccurateTime()) + 1000 * 10;

      let signature: string;

      switch (networkType) {
        case NetworkType.EVM: {
          const message = {
            domain: {
              name: import.meta.env.VITE_VERIFYING_NAME_1CT,
              version: import.meta.env.VITE_VERIFYING_VERSION_1CT,
              verifyingContract: import.meta.env.VITE_VERIFYING_CONTRACT_1CT,
            },
            primaryType: 'SignerProof' as const,
            types: {
              SignerProof: [
                { name: 'approvedSigner', type: 'address' },
                { name: 'signerExpiry', type: 'uint256' },
              ],
            },
            message: {
              approvedSigner: signingAddr,
              signerExpiry: isEmbedded ? expiryTime : BigInt(expiryTime),
            },
          };
          if (isEmbedded) {
            const { signature: _signature } = await signPrivyTypedData(
              message,
              {
                address,
                uiOptions: { showWalletUIs: false },
              },
            );
            signature = _signature;
          } else {
            signature = await signTypedData(wagmiConfig, message);
          }
          break;
        }
        case NetworkType.SOLANA: {
          const encodedMessage = new TextEncoder().encode(
            JSON.stringify({
              approvedSigner: signingAddr,
              signerExpiry: expiryTime,
            }),
          );
          signature = uint8ArrayToHex(
            await signSolanaMessage({
              message: encodedMessage,
              options: {
                address,
                uiOptions: {
                  showWalletUIs: false,
                },
              },
            }),
          );
          break;
        }
      }

      const isRootWallet =
        !!primaryAddress &&
        address.toLowerCase() === primaryAddress.toLowerCase();
      const capitalStatus = getCapitalStatus(address);
      const isFunded =
        !isRootWallet && capitalStatus !== CapitalStatus.NOT_ELIGIBLE;

      const data = {
        networkType,
        signingAddr,
        primaryAddr: networkType ? address : address.toLowerCase(),
        signature,
        expiryTime,
        isMobile: false,
        ...(isFunded ? { isFunded } : {}),
      };

      let res;

      if (authenticated) {
        const token = await getAccessToken();
        if (!token) throw new Error('getAccessToken(): No token found');
        res = await cookieAwarePublicV2Api.post('/v2/register', {
          ...data,
          privy_access_token: token,
        });
      } else {
        res = await cookieAwarePublicV2Api.post('/v2/register', data);
      }

      return {
        ...res,
        signingKey: wallet.privateKey as `0x${string}`,
      };
    },
    onSuccess: (
      res: ExtendedRegisterResponse,
      variables: RegisterVariables,
    ) => {
      const { address, rememberMe } = variables;
      const { setAuthParams, setHasAuth, setHasPrimaryAuth } =
        useAccountStore.getState();

      sendEvent(EventType.ACCOUNT_REGISTERED);

      setAuthParams(address, {
        address,
        signingKey: res.signingKey,
        apiKey: res.apiKey,
        accountGroup: res.accGroup,
        shouldPersist: rememberMe,
      });

      updatePrivateApiHeaders({
        'X-API-KEY': res.apiKey,
      });

      setHasAuth(true);

      if (address === primaryAddress) {
        updatePrimaryPrivateApiHeaders({ 'X-API-KEY': res.apiKey });
        setHasPrimaryAuth(true);
      }

      const refCode = localStorage.getItem('ref-code');
      if (refCode) {
        submitRefCode(refCode);
      }
    },
    onError: (err) => {
      console.warn('Registration failed: ', err);
      if (!config.isLocal) {
        sendEvent(
          EventType.REGISTRATION_FAILED,
          (
            err as AxiosError<{
              detail: { msg: string; code: number };
            }>
          ).response?.data.detail ?? {
            msg: (err as Error).message,
            code: null,
          },
        );
        disconnect();
      }
    },
  });
}
